<script setup lang="ts">
    import { ref, onMounted, onUnmounted } from 'vue';
    import Contact from './Contact.vue';
    
    // Current Issue Variables
    const currentVolImageDeck = ref(["/images/Volume-2/Science.png", "/images/Volume-2/Arts.png"]);
    const currentVolCurrentImage = ref(currentVolImageDeck.value[0]);
    const currentVolPdfLinks = ref([
        "/files/JUI-Vol.02-Arts-Humanities.pdf",
        "/files/JUI-Vol.02-Science-Medicine.pdf"
    ]);
    const currentVolCurrentIndex = ref(0);
    const currentVolImageLoaded = ref(false);
    let currentVolIntervalId: any;
    const isArchiveOpen = ref(false);
    
    // Archive Volumes Variables
    const archiveVolumes = ref([
        {
            volume: 1,
            issue: 1,
            date: "September 2024",
            imageDeck: ["/images/Volume-1/Volume1-Science.png", "/images/Volume-1/Volume1-Arts.png"],
            currentImage: "/images/Volume-1/Volume1-Science.png",
            currentIndex: 0,
            pdfLinks: ["/files/JUI-Vol.01-Arts.pdf", "/files/JUI-Vol.01-Science.pdf"],
            intervalId: null as any
        }
    ]);
    
    const toWebp = (source: string) => source.replace(/\.(png|jpe?g)$/i, '.webp');

    const preloadImages = (sources: string[]) => {
        sources.forEach((source) => {
            const image = new Image();
            image.src = source;
            const webpSource = toWebp(source);
            if (webpSource !== source) {
                const webpImage = new Image();
                webpImage.src = webpSource;
            }
        });
    };

    onMounted(() => {
        preloadImages(currentVolImageDeck.value);
        archiveVolumes.value.forEach((vol) => preloadImages(vol.imageDeck));

        // Start rotation for current issue
        currentVolIntervalId = setInterval(() => rotateImages(2), 10000);
        
        // Start rotation for archive volumes
        archiveVolumes.value.forEach((vol) => {
            vol.intervalId = setInterval(() => rotateArchiveImage(vol), 3000);
        });
    });
    
    onUnmounted(() => {
        clearInterval(currentVolIntervalId);
        archiveVolumes.value.forEach((vol) => {
            if (vol.intervalId) clearInterval(vol.intervalId);
        });
    });
    
    //Function Declarations
    const rotateImages = (volume: number) => {
        if (volume === 2) {
            currentVolImageLoaded.value = false;
            currentVolCurrentIndex.value = (currentVolCurrentIndex.value + 1) % currentVolImageDeck.value.length;
            currentVolCurrentImage.value = currentVolImageDeck.value[currentVolCurrentIndex.value];
        }
    }
    
    const rotateArchiveImage = (vol: any) => {
        vol.currentIndex = (vol.currentIndex + 1) % vol.imageDeck.length;
        vol.currentImage = vol.imageDeck[vol.currentIndex];
    }
    
    const toggleArchive = () => {
        isArchiveOpen.value = !isArchiveOpen.value;
    }
    </script>
    <template>
        <div class="w-full h-full">
            <!-- Current Issue Section -->
            <div class="min-h-[30vh] sm:min-h-[40vh] bg-[#012331] flex items-center justify-center text-white font-satoshibold text-3xl md:text-5xl relative">
                <p>Current Issue</p>
            </div>
            <div class="h-full w-full flex flex-col items-center pt-10 pb-12">
                <div class="w-[80%] h-full flex justify-center">
                    <div class="h-full w-fit flex justify-center items-center flex flex-col space-y-4">
                        <picture class="inline lg:hidden">
                            <source :srcset="toWebp(currentVolCurrentImage)" type="image/webp" />
                            <img
                                class="image-container object-contain rounded-md shadow-lg border-4 border-[#012331] transition-opacity duration-300"
                                :class="currentVolImageLoaded ? 'opacity-100' : 'opacity-0'"
                                :src="currentVolCurrentImage"
                                alt="JUI Volume 2 cover"
                                loading="eager"
                                decoding="async"
                                fetchpriority="high"
                                @load="currentVolImageLoaded = true"
                            />
                        </picture>
                        <div class="hidden lg:flex w-full sm:w-[50%] flex flex-row justify-center gap-6">
                            <picture>
                                <source srcset="/images/Volume-2/Science.webp" type="image/webp" />
                                <img class="h-full max-h-[45rem] max-w-lg max-w-full w-auto object-contain shrink border-2 border-[#012331]/20 rounded-lg" src="/images/Volume-2/Science.png" alt="Science & Medicine Volume 2" loading="eager" decoding="async" fetchpriority="high" />
                            </picture>
                            <picture>
                                <source srcset="/images/Volume-2/Arts.webp" type="image/webp" />
                                <img class="h-full max-h-[45rem] max-w-lg max-w-full w-auto object-contain shrink border-2 border-[#012331]/20 rounded-lg" src="/images/Volume-2/Arts.png" alt="Arts & Humanities Volume 2" loading="eager" decoding="async" fetchpriority="high" />
                            </picture>
                        </div>
                        <div class="w-full flex flex-col items-center space-y-6 text-center">
                            <p class="font-pragmaticabold text-xl sm:text-2xl">
                                JUI Volume 2, Issue 1
                            </p>
                            <div class="flex flex-col sm:flex-row gap-4 sm:gap-6 font-satoshi">
                                <a 
                                    :href="currentVolPdfLinks[1]" 
                                    target="_blank"
                                    class="px-6 py-3 bg-[#e68c74] text-white rounded-lg font-satoshibold transition-all duration-300 hover:bg-[#e68c74]/90 hover:shadow-lg active:scale-95"
                                >
                                    Science & Medicine PDF
                                </a>
                                <a 
                                    :href="currentVolPdfLinks[0]" 
                                    target="_blank"
                                    class="px-6 py-3 bg-[#e68c74] text-white rounded-lg font-satoshibold transition-all duration-300 hover:bg-[#e68c74]/90 hover:shadow-lg active:scale-95"
                                >
                                    Arts & Humanities PDF
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    
            <!-- Archives Section -->
            <div class="w-full border-t-2 border-b-2 flex flex-col items-center justify-center">
                <button @click="toggleArchive" class="w-[90%] sm:w-[70%] py-4 flex items-center justify-between hover:bg-gray-100 transition-colors duration-200 rounded-lg">
                    <h2 class="text-3xl font-satoshibold text-[#012331] mx-2">Archive</h2>
                    <svg 
                        class="w-6 h-6 text-[#012331] transition-transform duration-300"
                        :class="{ 'rotate-180': isArchiveOpen }"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                
                <div 
                    v-show="isArchiveOpen"
                    class="overflow-hidden transition-all duration-300 sm:w-[50%] mx-auto"
                >
                    <div class="py-6 px-4 sm:px-8 space-y-6">
                        <div 
                            v-for="vol in archiveVolumes" 
                            :key="vol.volume"
                            class="w-full flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200"
                        >
                            <!-- Archive Image -->
                            <picture>
                                <source :srcset="toWebp(vol.currentImage)" type="image/webp" />
                                <img
                                    class="archive-image-container flex-shrink-0 object-contain rounded-md shadow-md border-2 border-[#012331]"
                                    :src="vol.currentImage"
                                    :alt="`JUI Volume ${vol.volume} cover`"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </picture>
                            <!-- Archive Content -->
                            <div class="flex-1 flex flex-col justify-center sm:justify-start space-y-3 sm:space-y-4">
                                <div>
                                    <p class="font-pragmaticabold text-lg sm:text-xl text-[#012331]">
                                        JUI Volume {{ vol.volume }}, Issue {{ vol.issue }}
                                    </p>
                                    <p class="font-satoshi text-sm sm:text-base text-gray-600">
                                        Published {{ vol.date }}
                                    </p>
                                </div>
                                <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 font-satoshi">
                                    <a 
                                        :href="vol.pdfLinks[1]" 
                                        target="_blank"
                                        class="px-4 py-2 sm:px-5 sm:py-2.5 bg-[#012331] text-white rounded-lg font-satoshibold text-sm sm:text-base transition-all duration-300 hover:bg-[#012331]/90 hover:shadow-md active:scale-95 text-center"
                                    >
                                        Science & Medicine PDF
                                    </a>
                                    <a 
                                        :href="vol.pdfLinks[0]" 
                                        target="_blank"
                                        class="px-4 py-2 sm:px-5 sm:py-2.5 bg-[#012331] text-white rounded-lg font-satoshibold text-sm sm:text-base transition-all duration-300 hover:bg-[#012331]/90 hover:shadow-md active:scale-95 text-center"
                                    >
                                        Arts & Humanities PDF
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    
            <!-- Contact Section -->
            <div class="flex justify-center py-6">
                <div class="w-[90%] sm:w-[70%]">
                    <Contact />
                </div>
            </div>
        </div>
    </template>
    
    <style scoped>
    .image-container {
        width: 34vh;
        height: 50vh;
        object-fit: cover;
    }
    
    .archive-image-container {
        width: 120px;
        height: 180px;
        object-fit: cover;
    }
    
    @media screen and (min-width: 640px) {
        .archive-image-container {
            width: 140px;
            height: 210px;
        }
    }
    
    @media screen and (min-width: 768px) {
        .image-container {
            width: 44vh;
            height: 65vh;
        }
        
        .archive-image-container {
            width: 160px;
            height: 240px;
        }
    }
    </style>